import { useMemo, useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import {
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CalendarClock,
  ArrowDownToLine,
  ArrowUpFromLine,
  MoveHorizontal,
  Lock,
} from 'lucide-react'
import { format, startOfMonth, endOfMonth, addMonths, getDate, getDay } from 'date-fns'
import { it } from 'date-fns/locale'
import { getAvailabilityGrid, type AvailabilityInfo } from '../../lib/api/client'
import { getContentList } from '../../lib/api/content'
import { DayModal } from './DayModal'
import { cn } from '../../lib/cn'

const SERVICE_TAB = 'service' as const
type TabKey = number | typeof SERVICE_TAB
const TABS: { key: TabKey; label: string }[] = [
  { key: 1, label: 'Auto' },
  { key: 2, label: 'Moto' },
  { key: SERVICE_TAB, label: 'Servizi' },
]
const DOW = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
const HATCH = 'repeating-linear-gradient(45deg,#f0ecdf,#f0ecdf 5px,#e7e2d2 5px,#e7e2d2 10px)'

const UNLIMITED = 9999
const isSane = (n: number | null | undefined) => typeof n === 'number' && n > 0 && n < UNLIMITED && n !== -999
const saneMax = (...vals: (number | null | undefined)[]): number => {
  for (const v of vals) if (isSane(v)) return v as number
  return UNLIMITED
}
const toNum = (v: unknown): number | undefined => {
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}
const fmtCap = (n: number) => (n >= UNLIMITED ? '∞' : String(n))

type Cell = {
  has: boolean
  finite: boolean
  cap: number
  occ: number
  avail: number
  availPct: number
  occPct: number
  maxOnSite: number
  onsiteAvail: number
  in: number
  out: number
  still: number
  closed: boolean
}

function cellOf(info: AvailabilityInfo | null, capCfg?: number, onsiteCfg?: number): Cell {
  const zero: Cell = { has: false, finite: false, cap: 0, occ: 0, avail: 0, availPct: 0, occPct: 0, maxOnSite: 0, onsiteAvail: 0, in: 0, out: 0, still: 0, closed: false }
  if (!info) return zero
  const cap = saneMax(info.maxAllowed, capCfg)
  const occ = info.occupiedParkingSpaces ?? 0
  const maxOnSite = saneMax(info.maxOnSite, onsiteCfg)
  const onsiteBooked = info.book && typeof info.book === 'object' ? info.book.onsite : 0
  const io = info.inAndOut ?? { in: 0, out: 0, still: 0 }
  const finite = cap > 0 && cap < UNLIMITED
  const avail = cap - occ
  return {
    has: true,
    finite,
    cap,
    occ,
    avail,
    availPct: finite ? Math.max(0, Math.round((avail / cap) * 100)) : 100,
    occPct: finite ? Math.round((occ / cap) * 100) : 0,
    maxOnSite,
    onsiteAvail: maxOnSite - onsiteBooked,
    in: io.in,
    out: io.out,
    still: io.still,
    closed: Boolean(info.closed),
  }
}
// occupancy bands: the bar shows how full the day is (green → amber → red as it fills)
function band(occPct: number) {
  if (occPct >= 95) return { cell: 'bg-venetian-soft border-[#f3c6cd]', bar: 'bg-venetian' }
  if (occPct >= 85) return { cell: 'bg-oro-soft border-[#f0e0b0]', bar: 'bg-oro' }
  return { cell: 'bg-card border-line', bar: 'bg-ok' }
}
const cap1 = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

function Stat({ label, value, sub, danger }: { label: string; value: string; sub?: string; danger?: boolean }) {
  return (
    <div className="cv-panel px-4 py-3.5">
      <div className="text-[11px] font-bold uppercase tracking-[1.4px] text-muted">{label}</div>
      <div className={cn('mt-1 font-display text-[24px] font-bold leading-none tracking-[-.5px]', danger ? 'text-venetian' : 'text-coal')}>
        {value}
      </div>
      {sub && <div className="mt-1 text-[12px] text-muted">{sub}</div>}
    </div>
  )
}

function IO({ icon: Icon, value, title, tone }: { icon: typeof ArrowDownToLine; value: number; title: string; tone: string }) {
  return (
    <span className={cn('flex items-center gap-1 text-[13px] font-semibold tabular-nums', tone)} title={title}>
      <Icon className="h-4 w-4" /> {value}
    </span>
  )
}

export function AvailabilityPage() {
  const [anchor, setAnchor] = useState(() => startOfMonth(new Date()))
  const [tab, setTab] = useState<TabKey>(1)
  const [modalDate, setModalDate] = useState<string | null>(null)
  const isService = tab === SERVICE_TAB

  const monthStart = anchor
  const monthEnd = endOfMonth(anchor)
  const startStr = format(monthStart, 'yyyy-MM-dd')
  const endStr = format(monthEnd, 'yyyy-MM-dd')
  const todayStr = format(new Date(), 'yyyy-MM-dd')

  const grid = useQuery({
    queryKey: ['availabilityGrid', startStr, endStr],
    queryFn: () => getAvailabilityGrid(startStr, endStr),
    placeholderData: keepPreviousData,
  })
  // configured caps & labels — the grid uses the 9999 baseline for some fields, so the real
  // numbers (e.g. on-site 220/25) come from the transport/service config.
  const transports = useQuery({
    queryKey: ['opt', 'transport'],
    queryFn: () => getContentList('transport', { limit: 50 }),
    staleTime: Infinity,
  })
  const servicesQ = useQuery({
    queryKey: ['opt', 'service'],
    queryFn: () => getContentList('service', { limit: 50 }),
    staleTime: Infinity,
  })
  const transportCfg = (id: number) => {
    const r = transports.data?.data?.find((t) => String(t.id) === String(id))
    return {
      cap: toNum(r?.elements.maxAvailability),
      onsite: toNum(r?.elements.maxOnsitePaymentAvailability),
    }
  }
  const serviceLabel = (id: number) => {
    const r = servicesQ.data?.data?.find((s) => String(s.id) === String(id))
    return r ? String((r.elements.name as string) ?? (r.elements.label as string) ?? `Servizio ${id}`) : `Servizio ${id}`
  }
  const serviceCap = (id: number) => toNum(servicesQ.data?.data?.find((s) => String(s.id) === String(id))?.elements.maxAvailability)

  const dStr = (d: number) => format(new Date(anchor.getFullYear(), anchor.getMonth(), d), 'yyyy-MM-dd')
  const transportInfo = (ds: string): AvailabilityInfo | null =>
    grid.data?.[ds]?.info?.find((i) => i.type === 'TRANSPORT' && i.id === tab) ?? null
  const serviceItems = (ds: string): AvailabilityInfo[] => grid.data?.[ds]?.info?.filter((i) => i.type === 'SERVICE') ?? []

  const daysInMonth = getDate(monthEnd)
  const lead = (getDay(monthStart) + 6) % 7
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth])

  const tcfg = !isService && typeof tab === 'number' ? transportCfg(tab) : { cap: undefined, onsite: undefined }

  const stats = useMemo(() => {
    let k1 = 0
    let sumPct = 0
    let open = 0
    let quasi = 0
    let chiusi = 0
    if (isService) {
      const types = new Set<number>()
      for (const d of days) {
        for (const s of serviceItems(dStr(d))) {
          types.add(s.id)
          if (s.closed) {
            chiusi++
            continue
          }
          const cap = saneMax(s.maxAllowed, serviceCap(s.id))
          const book = typeof s.book === 'number' ? s.book : 0
          if (cap > 0 && cap < UNLIMITED) {
            const pct = Math.round((book / cap) * 100)
            open++
            sumPct += pct
            if (pct >= 90) quasi++
          }
        }
      }
      k1 = types.size
    } else {
      for (const d of days) {
        const c = cellOf(transportInfo(dStr(d)), tcfg.cap, tcfg.onsite)
        if (c.finite) k1 = Math.max(k1, c.cap)
        if (c.closed) chiusi++
        else if (c.has) {
          open++
          sumPct += c.occPct
          if (c.occPct >= 90) quasi++
        }
      }
    }
    return { k1, occMedia: open ? Math.round(sumPct / open) : 0, quasi, chiusi }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grid.data, tab, anchor, days, transports.data, servicesQ.data])

  const monthLabel = cap1(format(anchor, 'LLLL yyyy', { locale: it }))
  const isCurrentMonth = startStr === format(startOfMonth(new Date()), 'yyyy-MM-dd')

  return (
    <div className="w-full px-6 pb-16 pt-7 lg:px-9">
      {/* page-head */}
      <div className="mb-6 flex flex-wrap items-end gap-4">
        <div>
          <h2 className="font-display text-[26px] font-bold leading-tight tracking-[-.3px] text-coal sm:text-[28px]">
            Disponibilità
          </h2>
          <p className="mt-1.5 max-w-[70ch] text-[13.5px] text-muted">
            {isService
              ? 'Posti disponibili per servizio accessorio. Clicca un giorno per modificarne la capienza o chiuderlo.'
              : 'Posti disponibili su capienza per tipo di veicolo. A destra i pagamenti in cassa; in basso ingressi, uscite e stanziali. La barra e la percentuale mostrano l’occupazione. Clicca un giorno per modificarlo.'}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2.5 sm:ml-auto sm:w-auto">
          <div className="inline-flex overflow-hidden rounded-lg border border-line bg-card">
            {TABS.map((t) => (
              <button
                key={String(t.key)}
                onClick={() => setTab(t.key)}
                className={cn(
                  'px-4 py-2 text-[13px] font-semibold transition-colors',
                  tab === t.key ? 'bg-lagoon text-white' : 'text-muted hover:text-coal',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-1 rounded-lg border border-line bg-card p-1 sm:ml-0">
            <button onClick={() => setAnchor((a) => addMonths(a, -1))} className="rounded-md px-1.5 py-1 text-muted hover:bg-paper hover:text-coal" title="Mese precedente">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="min-w-[120px] px-1 text-center text-[13px] font-semibold text-coal">{monthLabel}</span>
            <button onClick={() => setAnchor((a) => addMonths(a, 1))} className="rounded-md px-1.5 py-1 text-muted hover:bg-paper hover:text-coal" title="Mese successivo">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {!isCurrentMonth && (
            <button
              onClick={() => setAnchor(startOfMonth(new Date()))}
              className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-card px-3 py-[9px] text-[13px] font-semibold text-slate hover:border-faint"
            >
              <CalendarClock className="h-4 w-4" /> Oggi
            </button>
          )}
        </div>
      </div>

      {/* stat cards */}
      <div className="mb-[18px] grid grid-cols-2 gap-[14px] lg:grid-cols-4">
        {isService ? (
          <Stat label="Tipi di servizio" value={stats.k1 ? String(stats.k1) : '—'} sub="servizi attivi" />
        ) : (
          <Stat label="Capienza giornaliera" value={stats.k1 ? fmtCap(stats.k1) : '—'} sub="posti vendibili (max)" />
        )}
        <Stat label="Occupazione media" value={`${stats.occMedia}%`} sub="nel mese, giorni aperti" />
        <Stat label="Giorni quasi pieni" value={String(stats.quasi)} sub="≥ 90% occupati" danger={stats.quasi > 0} />
        <Stat label={isService ? 'Chiusure servizi' : 'Giorni chiusi'} value={String(stats.chiusi)} sub="chiusi alla vendita" />
      </div>

      {/* calendar */}
      <div className="cv-panel p-4 sm:p-5">
        {grid.isError ? (
          <div className="px-4 py-16 text-center text-sm text-muted">
            <AlertCircle className="mx-auto mb-2 h-5 w-5 text-venetian" /> Impossibile caricare la disponibilità.
          </div>
        ) : (
          <div className="relative">
            {grid.isFetching && (
              <div className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center pt-14">
                <span className="inline-flex items-center gap-3 rounded-xl border border-line bg-card px-5 py-3 text-[13.5px] font-semibold text-coal shadow-lg">
                  <span className="h-5 w-5 animate-spin rounded-full border-[3px] border-line border-t-lagoon" />
                  Caricamento disponibilità…
                </span>
              </div>
            )}
            <div className={cn('overflow-x-auto transition-opacity duration-200', grid.isFetching && 'opacity-60')}>
            <div className="min-w-[760px]">
              <div className="grid grid-cols-7 gap-1.5">
                {DOW.map((d) => (
                  <div key={d} className="pb-1 text-center text-[11px] font-bold uppercase tracking-[1px] text-muted">
                    {d}
                  </div>
                ))}
                {Array.from({ length: lead }).map((_, i) => (
                  <div key={`lead-${i}`} className="min-h-[132px] rounded-lg bg-paper/40" />
                ))}
                {days.map((d) => {
                  const ds = dStr(d)
                  const isToday = ds === todayStr
                  const header = (
                    <div className={cn('flex items-center justify-between px-2.5 py-1.5', isToday ? 'bg-coal text-white' : 'bg-black/[0.03] text-coal')}>
                      {isToday ? <span className="text-[10px] font-bold uppercase tracking-wider">oggi</span> : <span />}
                      <span className="text-[15px] font-bold">{d}</span>
                    </div>
                  )

                  // ── SERVICE view ─────────────────────────────
                  if (isService) {
                    const svc = serviceItems(ds)
                    return (
                      <button key={d} onClick={() => setModalDate(ds)} className="flex min-h-[132px] flex-col overflow-hidden rounded-lg border border-line bg-card text-left transition-colors hover:border-faint">
                        {header}
                        {grid.isLoading ? (
                          <div className="flex-1 p-2"><div className="h-3.5 w-16 animate-pulse rounded bg-line-soft" /></div>
                        ) : svc.length === 0 ? (
                          <div className="flex flex-1 items-center justify-center text-[11px] text-faint">—</div>
                        ) : (
                          <div className="flex flex-1 flex-col gap-1.5 p-2.5">
                            {svc.map((s) => {
                              const cap = saneMax(s.maxAllowed, serviceCap(s.id))
                              const book = typeof s.book === 'number' ? s.book : 0
                              const avail = cap - book
                              const finite = cap > 0 && cap < UNLIMITED
                              const full = finite && avail <= 0
                              return (
                                <div key={s.id} className="flex items-center justify-between gap-1.5 text-[12.5px]">
                                  <span className={cn('truncate', s.closed ? 'text-faint line-through' : 'text-slate')}>{serviceLabel(s.id)}</span>
                                  <span className={cn('shrink-0 font-mono tnum', s.closed ? 'text-faint' : full ? 'text-venetian' : 'text-coal')}>
                                    {s.closed ? '—' : finite ? `${avail}/${cap}` : `${book} pren.`}
                                  </span>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </button>
                    )
                  }

                  // ── TRANSPORT view ───────────────────────────
                  const c = cellOf(transportInfo(ds), tcfg.cap, tcfg.onsite)
                  const b = band(c.occPct)
                  const full = c.finite && c.avail <= 0
                  const showOnsite = c.has && c.maxOnSite < UNLIMITED
                  return (
                    <button
                      key={d}
                      onClick={() => setModalDate(ds)}
                      className={cn('flex min-h-[132px] flex-col overflow-hidden rounded-lg border text-left transition-colors hover:border-faint', c.closed ? 'border-[#e7e2d2]' : b.cell)}
                      style={c.closed ? { background: HATCH } : undefined}
                    >
                      {header}
                      {grid.isLoading ? (
                        <div className="flex-1 p-2.5"><div className="h-4 w-16 animate-pulse rounded bg-line-soft" /></div>
                      ) : c.closed ? (
                        <div className="flex flex-1 items-center justify-center gap-1.5 text-[12.5px] font-bold uppercase tracking-wide text-slate/70">
                          <Lock className="h-4 w-4" /> Chiuso
                        </div>
                      ) : c.has ? (
                        <div className="flex flex-1 flex-col p-2.5">
                          <div className="flex items-baseline justify-between gap-1.5">
                            <span className={cn('font-mono text-[22px] font-bold leading-none tnum', full ? 'text-venetian' : 'text-coal')}>
                              {c.finite ? (
                                <>
                                  {c.avail}
                                  <span className="text-[14px] font-semibold text-muted">/{c.cap}</span>
                                </>
                              ) : (
                                '∞'
                              )}
                            </span>
                            {showOnsite && (
                              <span className="font-mono text-[12.5px] tnum text-faint" title="Pagamenti in cassa: disponibili / max">
                                {c.onsiteAvail}/{c.maxOnSite}
                              </span>
                            )}
                          </div>
                          {c.finite && (
                            <div className="mt-2.5 flex items-center gap-2">
                              <div className="h-1.5 flex-1 overflow-hidden rounded-sm bg-paper">
                                <div className={cn('h-full rounded-sm', b.bar)} style={{ width: `${c.occPct}%` }} />
                              </div>
                              <span className="w-9 shrink-0 text-right text-[11.5px] font-semibold tnum text-muted">{c.occPct}%</span>
                            </div>
                          )}
                          <div className="mt-auto flex items-center justify-between gap-1 border-t border-line-soft pt-2">
                            <IO icon={ArrowDownToLine} value={c.in} title="Ingressi" tone="text-ok" />
                            <IO icon={ArrowUpFromLine} value={c.out} title="Uscite" tone="text-canal" />
                            <IO icon={MoveHorizontal} value={c.still} title="Stanziali" tone="text-viola" />
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-1 items-center justify-center text-[12px] text-faint">—</div>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
            </div>
          </div>
        )}

        {/* legend */}
        <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-muted">
          <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded-[3px] bg-ok" /> Normale</span>
          <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded-[3px] bg-oro" /> Occupazione ≥ 85%</span>
          <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded-[3px] bg-venetian" /> Quasi pieno (≥ 95%)</span>
          <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded-[3px] border border-[#e7e2d2]" style={{ background: HATCH }} /> Chiuso</span>
          {!isService && (
            <span className="ml-auto flex items-center gap-3">
              <span className="flex items-center gap-1 font-medium text-ok"><ArrowDownToLine className="h-3.5 w-3.5" /> ingressi</span>
              <span className="flex items-center gap-1 font-medium text-canal"><ArrowUpFromLine className="h-3.5 w-3.5" /> uscite</span>
              <span className="flex items-center gap-1 font-medium text-viola"><MoveHorizontal className="h-3.5 w-3.5" /> stanziali</span>
            </span>
          )}
        </div>
      </div>

      <DayModal
        date={modalDate}
        preferLabel={isService ? 'Servizi' : 'Tipi di veicoli'}
        onClose={() => setModalDate(null)}
        onNavigate={(d) => setModalDate(d)}
      />
    </div>
  )
}
